import { zodResolver } from '@hookform/resolvers/zod';
import { Layers } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useUpdatePlanDetails } from '../hooks/usePlanMutations';
import { usePlanById } from '../hooks/usePlans';
import { editPlanLimitsSchema, type EditPlanLimitsFormValues } from '../schemas/plan-schema';
import type { UpdatePlanDetailsRequest } from '../types/plans.types';
import { PlanLimitsFields } from './PlanLimitsFields';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { FormControl, FormField, FormItem } from '@/shared/components/ui/form';
import { Input, LabelVariants, MessageVariants } from '@/shared/components/ui/input';
import { DialogFooter as ModalFooter, ModalForm } from '@/shared/components/ui/modal-form';
import { Textarea } from '@/shared/components/ui/textarea';
import { UnifiedButton } from '@/shared/components/ui/unified-button';

interface EditPlanLimitsModalProps {
  planId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_VALUES: EditPlanLimitsFormValues = {
  name: '',
  description: '',
  maxUsers: 0,
  maxRegions: 0,
  maxActiveTopics: 0,
  maxActiveActors: 0,
  maxAiAnalysisMonthly: 0,
  maxAlerts: 0,
  maxScheduledReports: 0,
};

export function EditPlanLimitsModal({ planId, open, onOpenChange }: EditPlanLimitsModalProps) {
  const { plan, isLoading } = usePlanById(open ? planId : '');
  const { updatePlanDetails, isUpdating } = useUpdatePlanDetails(planId);

  const form = useForm<EditPlanLimitsFormValues>({
    resolver: zodResolver(editPlanLimitsSchema),
    defaultValues: EMPTY_VALUES,
  });

  const { reset } = form;

  useEffect(() => {
    if (!plan) return;
    reset({
      name: plan.name,
      description: plan.description ?? '',
      maxUsers: plan.maxUsers,
      maxRegions: plan.maxRegions,
      maxActiveTopics: plan.maxActiveTopics,
      maxActiveActors: plan.maxActiveActors,
      maxAiAnalysisMonthly: plan.maxAiAnalysisMonthly,
      maxAlerts: plan.maxAlerts,
      maxScheduledReports: plan.maxScheduledReports,
    });
  }, [plan, reset]);

  const handleClose = () => {
    if (!isUpdating) {
      onOpenChange(false);
    }
  };

  const onSubmit = (values: EditPlanLimitsFormValues) => {
    const payload: UpdatePlanDetailsRequest = {
      ...values,
      description: values.description || undefined,
    };

    updatePlanDetails(payload, {
      onSuccess: () => {
        Notification.success({ title: 'Plan actualizado exitosamente' });
        onOpenChange(false);
      },
    });
  };

  const isBusy = isLoading || isUpdating;

  return (
    <ModalForm
      open={open}
      headerIcon={<Layers />}
      onOpenChange={handleClose}
      title="Editar plan"
      description="Cambia el nombre, la descripción y los límites."
      className="sm:max-w-[560px]"
      data-testid="edit-plan-modal"
      form={form}
      onSubmit={onSubmit}
      footer={
        <ModalFooter>
          <div className="flex flex-row justify-end w-full gap-4">
            <UnifiedButton
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isBusy}
              data-testid="cancel-edit-plan-btn"
            >
              Cancelar
            </UnifiedButton>
            <UnifiedButton
              type="submit"
              variant="outline"
              actionType="submit"
              disabled={isBusy}
              data-testid="update-plan-btn"
            >
              Guardar
            </UnifiedButton>
          </div>
        </ModalFooter>
      }
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormControl>
              <Input
                label="Nombre"
                disabled={isBusy}
                error={form.formState.errors.name?.message}
                data-testid="edit-plan-name-input"
                {...field}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          // relative: LabelVariants es absolute, y sin ancestro posicionado la etiqueta se
          // anclaba al DialogContent y aterrizaba sobre el titulo del modal.
          <FormItem className="relative flex w-full flex-col gap-1">
            <label className={LabelVariants()}>Descripción</label>
            <FormControl>
              <Textarea
                placeholder="Opcional"
                rows={2}
                disabled={isBusy}
                data-testid="edit-plan-description-input"
                {...field}
              />
            </FormControl>
            {form.formState.errors.description?.message && (
              <span className={MessageVariants({ variant: 'error' })}>
                {form.formState.errors.description.message}
              </span>
            )}
          </FormItem>
        )}
      />

      <p className="text-primary text-xs">Límites del plan</p>
      <PlanLimitsFields form={form} disabled={isBusy} testIdPrefix="edit-plan" />

      {/* Los precios no se editan aquí a propósito: cambiarlos exige un precio nuevo en
          Stripe y puede afectar el cobro de las cuentas suscritas. */}
      <p data-testid="edit-plan-scope-hint" className="text-primary text-xs">
        Los cambios aplican solo a contrataciones nuevas: las cuentas ya suscritas conservan los
        límites que contrataron. Los precios se cambian por separado.
      </p>
    </ModalForm>
  );
}
